import { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cPath } from "../../constants";

const Bar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: hsl(${({ theme }) => theme.colors.navDark});
  border-top: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.1);
  padding: 1rem;
`;

const Inner = styled.div`
  max-width: ${({ theme }) => theme.layout.shellMax};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.shellPadX};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const TextBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
`;

const Text = styled.p`
  margin: 0;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.8);
`;

const PolicyLink = styled(Link)`
  color: hsl(${({ theme }) => theme.colors.accent});
  text-decoration: underline;
`;

const BtnRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const PrimaryBtn = styled.button`
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 600;
  &:hover {
    opacity: 0.9;
  }
`;

const GhostBtn = styled.button`
  border: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.2);
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.7);
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 500;
  &:hover {
    background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.05);
  }
`;

const CloseBtn = styled.button`
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.5);
  &:hover {
    color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  }
`;

export default function CookieConsent() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("cookie-consent") === "true";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const accept = () => {
    try {
      localStorage.setItem("cookie-consent", "true");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <Bar>
      <Inner>
        <TextBlock>
          <Cookie size={20} strokeWidth={2} style={{ flexShrink: 0 }} />
          <Text>
            {t("cookies.message")}{" "}
            <PolicyLink to={cPath("privacy")}>{t("cookies.privacyPolicy")}</PolicyLink>.
          </Text>
        </TextBlock>
        <BtnRow>
          <PrimaryBtn type="button" onClick={accept}>
            {t("cookies.acceptAll")}
          </PrimaryBtn>
          <GhostBtn type="button" onClick={accept}>
            {t("cookies.necessaryOnly")}
          </GhostBtn>
          <CloseBtn type="button" onClick={accept} aria-label="Dismiss">
            <X size={16} strokeWidth={2} />
          </CloseBtn>
        </BtnRow>
      </Inner>
    </Bar>
  );
}
